import { useLocation, useNavigate } from "@tanstack/react-router";
import { BiBookReader } from "react-icons/bi";
import {
  LuBookOpen,
  LuBrainCircuit,
  LuChartLine,
  LuCircleAlert,
  LuCoins,
  LuFlaskConical,
  LuLayoutTemplate,
  LuLibraryBig,
  LuMic,
  LuSchool,
  LuSearchCheck,
  LuSmartphone,
  LuUsers,
} from "react-icons/lu";
import { RiUserSettingsLine } from "react-icons/ri";
import type { NavItem } from "./Sidebar";
import { Sidebar } from "./Sidebar";

const navItems: NavItem[] = [
  {
    label: "Home",
    icon: <LuLibraryBig fontSize={24} />,
    to: "/admin",
  },
  {
    label: "Words",
    icon: <BiBookReader fontSize={24} color="" />,
    to: "/admin/words",
  },
  {
    label: "Books",
    icon: <LuBookOpen fontSize={24} />,
    to: "/admin/books",
  },
  {
    label: "Schools",
    icon: <LuSchool fontSize={24} />,
    to: "/admin/schools",
    matchPaths: ["/admin/classrooms"],
  },
  {
    label: "Preset",
    icon: <LuLayoutTemplate fontSize={24} />,
    to: "/admin/presets",
  },
  {
    label: "Access",
    icon: <LuSearchCheck fontSize={24} />,
    to: "/admin/access",
  },
  {
    label: "APKs",
    icon: <LuSmartphone fontSize={24} />,
    to: "/admin/apks",
  },
];

const settingsNavItems: NavItem[] = [
  {
    label: "Users",
    icon: <LuUsers fontSize={20} />,
    to: "/admin/users",
  },
  {
    label: "FSRS",
    icon: <LuBrainCircuit fontSize={20} />,
    to: "/admin/fsrs",
  },
  {
    label: "Analytics",
    icon: <LuChartLine fontSize={20} />,
    to: "/admin/analytics",
  },
  {
    label: "AI Cost",
    icon: <LuCoins fontSize={20} />,
    to: "/admin/ai-usage",
  },
  {
    label: "Errors",
    icon: <LuCircleAlert fontSize={20} />,
    to: "/admin/errors",
  },
  {
    label: "Admin",
    icon: <RiUserSettingsLine fontSize={20} />,
    to: "/admin/service-settings",
  },
  {
    label: "Speech",
    icon: <LuMic fontSize={20} />,
    to: "/admin/speech",
  },
  {
    label: "Phoneme",
    icon: <LuFlaskConical fontSize={20} />,
    to: "/admin/phoneme-lab",
  },
];

/**
 * Main sidebar component with predefined navigation items
 */
export function MainSidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine active item from current route
  // Check non-Home items first, then default to Home
  const activeItem =
    [...navItems, ...settingsNavItems].find((item) => {
      // Skip Home - it's the fallback
      if (item.to === "/admin") {
        return false;
      }
      // Path starts with the item's path, or one of the extra prefixes it owns
      return [item.to, ...(item.matchPaths ?? [])].some((path) =>
        location.pathname.startsWith(path)
      );
    })?.label || "Home";

  const handleClick = (to: string) => {
    navigate({ to });
  };

  return (
    <Sidebar activeItem={activeItem} settingsItems={settingsNavItems} onNavigate={handleClick}>
      {navItems.map((item) => {
        const isActive = activeItem === item.label;

        return (
          <button
            key={item.label}
            type="button"
            onClick={() => handleClick(item.to)}
            aria-label={item.label}
            aria-current={isActive ? "page" : undefined}
            className={`nav-glow-effect ${isActive ? "nav-item-active" : "nav-item-inactive"} w-16 h-16 flex-col`}
          >
            {item.icon}
            <span className="caption">{item.label}</span>
          </button>
        );
      })}
    </Sidebar>
  );
}
